import type { ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PhotoTagEntityType, SystemRole, hasMinimumRole } from '@peles/shared';
import { FileExplorer } from '@speles7172/file-console';
import '@speles7172/file-console/styles.css';
import { useAuth } from '../../../context/AuthContext';
import { useEvent, useEventUsers } from '../../../hooks/useEvents';
import { BookingList } from '../../bookings/BookingList';
import { ExpenseListView } from '../../expenses/ExpenseListView';
import { ItemizedListView } from '../../planning/ItemizedListView';
import { NeedList } from '../../needs/NeedList';
import { SegmentedControl } from '../../common/SegmentedControl';
import { ClientLedgerView } from '../../client-payments';
import { InviteRsvpSection } from '../../invites/InviteRsvpSection';
import { CampaignWorkspace } from '../../invites/campaign/CampaignWorkspace';
import { PhotoListView } from '../../photos/PhotoListView';
import { RecordConversationList } from '../../messaging/RecordConversationList';
import { SeatingBoard } from '../../seating/SeatingBoard';
import { NotificationRoutingScreen } from '../../notifications/NotificationRoutingScreen';
import { FILE_ENTITY_TYPES } from '../../files/entityTypes';
import { FILE_UPLOAD_SOURCES } from '../../files/googleSources';
import { RequestFilesButton } from '../../files/RequestFilesButton';
import { fileTransport } from '../../../api/files';
import { useRememberedChoice } from '../../../hooks/useRememberedChoice';

/** A titled white card — how each block inside a tab is framed. */
export function TabCard({
  title,
  actions,
  children,
  className = '',
  id,
}: {
  id?: string;
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`bg-white rounded-2xl border border-gray-200 p-5 ${className}`}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 mb-4">
          {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

function useEventId(): string {
  return useParams<{ eventId: string }>().eventId!;
}

/** Who sits where: the seating board on one of the event's plans. */
export function SeatingTab() {
  return <SeatingBoard eventId={useEventId()} canWrite />;
}

export function InvitesTab() {
  return (
    <TabCard>
      <InviteRsvpSection eventId={useEventId()} />
    </TabCard>
  );
}

/** One invitation campaign on its own page, its step in the address. */
export function InviteCampaignTab() {
  const eventId = useEventId();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: event } = useEvent(eventId);
  return (
    <TabCard>
      <CampaignWorkspace
        key={campaignId}
        eventId={eventId}
        campaignId={campaignId!}
        event={
          event && {
            name: event.name,
            startAt: event.startAt,
            endAt: event.endAt,
            venueName: event.venueName,
            logoUrl: event.logoUrl,
          }
        }
        step={params.get('step')}
        onStepChange={(step) => setParams({ step }, { replace: true })}
        onBack={() => navigate(`/events/${eventId}/invites`)}
      />
    </TabCard>
  );
}

/** Who the event is buying from and what it costs, side by side. */
const VENDOR_VIEWS = [
  { id: 'plan', label: 'Plan' },
  { id: 'items', label: 'Items' },
  { id: 'expenses', label: 'Expenses' },
  { id: 'vendors', label: 'Vendors' },
] as const;
type VendorView = (typeof VENDOR_VIEWS)[number]['id'];
const VENDOR_VIEW_IDS = VENDOR_VIEWS.map((v) => v.id);

/**
 * Vendors & Expenses: one switch over four views of the same data — the Plan
 * (every need, sourcing and booked, by category; the default), the items the
 * needs are made of (a grid to type into), the booked lines with their
 * payments, and the event's vendors. The view is in the
 * address (`?view=`), so a link opens it where it was; with none, it opens on
 * the view this person chose last (`useRememberedChoice`), else the Plan.
 */
export function VendorsExpensesTab() {
  const eventId = useEventId();
  const { user } = useAuth();
  const { data: event } = useEvent(eventId);
  const [params, setParams] = useSearchParams();
  const [lastView, rememberView] = useRememberedChoice<VendorView>(
    'event-vendors-view',
    VENDOR_VIEW_IDS,
    'plan',
  );
  const asked = params.get('view');
  const view: VendorView = VENDOR_VIEW_IDS.includes(asked as VendorView)
    ? (asked as VendorView)
    : lastView;
  const eventDate = event?.startAt
    ? new Date(event.startAt).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : undefined;
  return (
    <div className="space-y-4">
      <SegmentedControl
        ariaLabel="Vendors and expenses view"
        options={VENDOR_VIEWS.map((v) => ({ id: v.id, label: v.label }))}
        value={view}
        onChange={(next) => {
          rememberView(next);
          setParams(
            (current) => {
              const p = new URLSearchParams(current);
              p.set('view', next);
              p.delete('need');
              p.delete('tab');
              return p;
            },
            { replace: true },
          );
        }}
      />
      {view === 'plan' && (
        <TabCard title="Plan">
          <NeedList
            eventId={eventId}
            eventName={event?.name ?? ''}
            currentUser={user ?? undefined}
          />
        </TabCard>
      )}
      {view === 'items' && (
        <TabCard title="Items">
          <ItemizedListView eventId={eventId} />
        </TabCard>
      )}
      {view === 'expenses' && (
        <TabCard title="Expenses">
          <ExpenseListView entityType="EVENT" entityId={eventId} currentUser={user ?? undefined} />
        </TabCard>
      )}
      {view === 'vendors' && (
        <TabCard title="Vendors">
          <BookingList
            eventId={eventId}
            eventName={event?.name ?? ''}
            eventDate={eventDate}
            venueId={event?.venueId ?? undefined}
            currentUser={user?.email ? { email: user.email, name: user.name } : undefined}
          />
        </TabCard>
      )}
    </div>
  );
}

export function MoneyTab() {
  return (
    <TabCard title="Client ledger">
      <ClientLedgerView eventId={useEventId()} />
    </TabCard>
  );
}

/**
 * Who in the office hears about this event: its own distribution groups, and
 * rules that replace the office-wide ones for this event only.
 */
export function NotificationsTab() {
  const eventId = useEventId();
  const { data: event } = useEvent(eventId);
  return (
    <TabCard>
      <NotificationRoutingScreen eventId={eventId} eventName={event?.name} />
    </TabCard>
  );
}

/**
 * The event's files and photos. The files are the `file-client` library under
 * this event's node — the same place conversation attachments and upload-link
 * answers land — so the explorer is rooted at `event:<id>` and `embedded`
 * drops the "all files" crumb (inside an event there is nothing above it).
 */
export function FilesPhotosTab() {
  const eventId = useEventId();
  const { user } = useAuth();
  const producer = user != null && hasMinimumRole(user.systemRole, SystemRole.PLANNER);
  return (
    <div className="space-y-6">
      <TabCard
        title="Documents"
        actions={producer ? <EventRequestFilesButton eventId={eventId} /> : undefined}
      >
        <div className="h-[32rem]">
          <FileExplorer
            transport={fileTransport}
            rootNodeId={`event:${eventId}`}
            embedded
            title={null}
            entityTypes={FILE_ENTITY_TYPES}
            sources={FILE_UPLOAD_SOURCES}
          />
        </div>
      </TabCard>
      <TabCard title="Photos">
        <PhotoListView
          entityType={PhotoTagEntityType.EVENT}
          entityId={eventId}
          limit={24}
          enableUpload
          currentUser={user || undefined}
        />
      </TabCard>
    </div>
  );
}

/**
 * "Request files" about the event itself, offering its clients — the people
 * an event's own files are usually asked of. Anybody else is typed in.
 */
function EventRequestFilesButton({ eventId }: { eventId: string }) {
  const { data: event } = useEvent(eventId);
  const { data: eventUsers = [] } = useEventUsers(eventId);
  return (
    <RequestFilesButton
      target={{
        entityType: 'EVENT',
        entityId: eventId,
        recordName: event?.name,
        suggestedRecipients: eventUsers.flatMap((eu) =>
          eu.userEmail ? [{ email: eu.userEmail, name: eu.userName, description: 'Client' }] : [],
        ),
        folderScope: { entityType: 'Event', entityId: eventId },
      }}
    />
  );
}

export function ConversationsTab() {
  return (
    <RecordConversationList entityType="Event" entityId={useEventId()} recordNoun="this event" />
  );
}
